import { Modal } from "antd";
import { AuthContext } from "context/AuthContext";
import { SignalContext } from "context/SignalContext";
import { DatabaseRecordInfo, DatabaseInfo } from "lib/types";
import { getCameraById } from "pages/dashboard/cases/service";
import { Playback } from "pages/dashboard/playback/playback";
import PlaybackControlBar from "pages/dashboard/playback/PlaybackControlBar";
import { useCommonService } from "pages/service";
import { useContext, useEffect, useState, useMemo, useRef } from "react";
import { SvgClose } from "ui/svgs";
import { useNavigate } from "react-router-dom";
import { USER_ROLE } from "lib/constants";
import {
  getDatabasesList,
  deleteDatabase,
  updateDatabase,
  updateRecord,
  getGptImageDesc,
} from "pages/dashboard/database/service";
import { uploadSign } from "pages/service";
import { STATES } from "lib/constants";
import { Form, Select, Input, Button } from "antd";
import { SvgImageUpload } from "ui/svgs";
import TabSelector from "pages/dashboard/audioLibrary/components/tabSelector";
import ImageUpload from "./image-upload";
import { showToast } from "ui/components/common";
import { ALERT_VEHICLE_CAR_COLOR } from "lib/constants";

const colors = [
  "black",
  "white",
  "gray",
  "red",
  "blue",
  "yellow",
  "orange",
  "brown",
  "green",
  "purple",
  "cyan",
  "pink",
];

export default function RecordModal({
  editType,
  record,
  visible,
  onClose,
  onSave,
  img,
}: {
  editType: "add" | "edit";
  record?: DatabaseRecordInfo;
  visible: boolean;
  onClose: () => void;
  onSave: (data: DatabaseRecordInfo) => void;
  img?: Blob;
}) {
  const [form] = Form.useForm();
  const [currentType, setCurrentType] = useState<"human" | "car" | "lpr">();
  const [databases, setDatabases] = useState<DatabaseInfo[]>();
  const [gptRaw, setGptRaw] = useState<string>();
  const [gptLoading, setGptLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoUploading, setAutoUploading] = useState(false);

  const reset = () => {
    form?.resetFields();
    setCurrentType("human");
    setGptRaw("");
    setSaving(false);
    setGptLoading(false);
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    queryDatabases("");
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    img && autoUploadAndGetDesc(img);
  }, [img, visible]);

  const autoUploadAndGetDesc = async (imgBlob: Blob) => {
    setAutoUploading(true);
    const res = await uploadSign(Math.random() + "-crop.jpg");
    if (res.status !== 200) {
      showToast(1, "Failed to get upload signaure. Please try again later");
      setAutoUploading(false);
      return;
    }
    const s3Key = res.data.key;
    const downloadUrl = res.data.downloadSignUrl;
    try {
      await fetch(res.data.uploadSignUrl, {
        method: "PUT",
        body: imgBlob,
        headers: {
          "Content-Type": "image/jpeg",
        },
      });
    } catch (e) {
      console.error(e);
      showToast(1, "Failed to upload image. Please try again later");
      setAutoUploading(false);
      return;
    }
    setAutoUploading(false);
    form.setFieldsValue({
      img: { url: downloadUrl, s3Key },
    });
    onUploaded({ key: s3Key, url: downloadUrl });
  };

  useEffect(() => {
    if (!record) {
      reset();
      return;
    }
    setCurrentType(record?.type || "human");
    setGptRaw(record?.gptRaw || "");
    form.resetFields();
    form.setFieldsValue({
      img: { url: record?.image?.url, key: record?.image?.key },
      name: record?.name,
      trackingGroupId: editType === "add" ? null : record?.trackingGroupId,
      desc: record?.description,
      ...record?.lpr,
    });
  }, [record, visible]);

  const queryDatabases = async (query: string) => {
    const response = await getDatabasesList(query);
    if (response.status !== 200) {
      showToast(1, "There was a problem fetching databases. Please try again.");
      return;
    }
    const data = response.data;
    setDatabases(data?.databases);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await updateRecord({
        ...record,
        name: values?.name,
        trackingGroupId: values?.trackingGroupId,
        lpr: currentType === "lpr" ? { ...values } : undefined,
        description: currentType !== "lpr" ? values?.desc : undefined,
        s3Key: currentType !== "lpr" ? values?.img?.key : undefined,
        gptRaw: gptRaw,
        type: currentType,
      });
      setSaving(false);
      if (res.status !== 200) {
        showToast(1, "There was a problem saving record. Please try again.");
        return;
      }
      showToast(0, "Record saved successfully!");
      onSave({ ...record, ...values });
    } catch (errorInfo) {
      console.log("Validate Failed:", errorInfo);
    }
  };

  const onUploaded = async ({ key, url }) => {
    setGptLoading(true);
    const res = await getGptImageDesc(key);
    setGptLoading(false);
    if (res.status !== 200) {
      showToast(1, "Failed to get image description.Please try again.");
      return;
    }

    form.setFieldsValue({
      img: { url, key },
      desc: res.data?.description,
    });
    setGptRaw(res.data?.gptRaw);
  };

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [...Array(currentYear - 2000 + 1).keys()].map(
      (year) => year + 2000,
    );
    return years.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));
  }, []);

  const colorsOptions = colors.map((color) => ({
    value: color,
    label: (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          className="rounded-[4px] w-4 h-4 mr-2"
          style={{
            backgroundColor: ALERT_VEHICLE_CAR_COLOR[color],
          }}
        />
        <span>{color}</span>
      </div>
    ),
  }));

  return (
    <Modal
      wrapClassName="database-modal"
      title={editType === "add" ? "Add Record" : "Edit Record"}
      open={visible}
      closable={false}
      destroyOnClose
      footer={
        <div className="mt-6 mb-2">
          <button
            className="rounded-2xl bg-white px-5 py-3.5 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => onClose()}
          >
            Cancel
          </button>
          <button
            className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <div
                className="ml-1 text-white  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Save"
            )}
          </button>
        </div>
      }
      onCancel={onClose}
      width={992}
    >
      <Form form={form} layout="vertical">
        <div className="flex h-full gap-6 relative group mt-4">
          {currentType !== "lpr" ? (
            <Form.Item
              name="img"
              rules={[{ required: true, message: "Please upload an image" }]}
            >
              {/* @ts-ignore */}
              <ImageUpload
                onChange={onUploaded}
                className="h-[317px] w-[452px]"
                uploadingControl={autoUploading}
              />
            </Form.Item>
          ) : (
            <div className="bg-[#EEEEEE] rounded-2xl flex flex-col items-center justify-center gap-2 h-[317px] w-[452px] text-[#222222] text-opacity-25">
              <SvgImageUpload />
              Drag and Drop Image Here or Choose a File
            </div>
          )}
          <div className="w-[468px]">
            <TabSelector
              options={[
                { label: "Human", value: "human" },
                { label: "Car", value: "car" },
                { label: "LPR", value: "lpr" },
              ]}
              disabled={editType === "edit"}
              value={record?.type ?? currentType}
              onChange={(type) => {
                setCurrentType(type);
                //form.resetFields();
              }}
            />
            <Form.Item
              label="Record Name"
              name="name"
              rules={[
                { required: true, message: "Please input the record name!" },
              ]}
            >
              <Input className="h-[54px] rounded-2xl" />
            </Form.Item>
            {currentType !== "lpr" ? (
              <>
                <Form.Item
                  label="Database"
                  name="trackingGroupId"
                  rules={[
                    { required: true, message: "Please select a database!" },
                  ]}
                >
                  <Select
                    placeholder="Select a database"
                    className="h-[54px] rounded-2xl"
                    options={databases?.map((database) => ({
                      label: database.name,
                      value: database.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <div className="flex items-center gap-2">
                      Description
                      {gptLoading && (
                        <div
                          className=" text-blue-500  inline-block h-3 w-3 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                          role="status"
                        ></div>
                      )}
                    </div>
                  }
                  name="desc"
                  rules={[
                    {
                      required: true,
                      message: "Please input the description",
                    },
                  ]}
                >
                  <Input.TextArea
                    disabled={gptLoading}
                    className="h-[54px] rounded-2xl"
                    rows={4}
                    maxLength={1024}
                  />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  label="Database"
                  name="trackingGroupId"
                  rules={[
                    { required: true, message: "Please select a Database!" },
                  ]}
                >
                  <Select
                    placeholder="Select a Database"
                    className="h-[54px] rounded-2xl"
                    options={databases?.map((database) => ({
                      label: database.name,
                      value: database.id,
                    }))}
                  />
                </Form.Item>
                <Form.Item label="Model" name="model">
                  <Input className="h-[54px] rounded-2xl" />
                </Form.Item>
                <Form.Item label="Color" name="color">
                  <Select
                    className="h-[54px] rounded-2xl"
                    placeholder="Select a color"
                    allowClear
                    options={colorsOptions}
                  ></Select>
                </Form.Item>
                <Form.Item label="Year" name="year">
                  <Select
                    allowClear
                    placeholder="Select a year"
                    className="h-[54px] rounded-2xl"
                    options={yearOptions}
                  />
                </Form.Item>
                <Form.Item label="LPR" name="licensePlate">
                  <Input className="h-[54px] rounded-2xl" />
                </Form.Item>
                <Form.Item label="State" name="state">
                  <Select
                    allowClear
                    className="h-[54px] rounded-2xl"
                    options={Object.entries(STATES)?.map(([key, value]) => ({
                      label: value + " (" + key + ")",
                      value: value,
                    }))}
                  />
                </Form.Item>
              </>
            )}
          </div>
        </div>
      </Form>
    </Modal>
  );
}
